import { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { CalendarPlus, Check, Copy, DoorOpen, Printer, RotateCcw, Sparkles, UserPlus, UserRound, type LucideIcon } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { get } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { printDocument } from '@/lib/print';
import { copyText } from '@/lib/utils';
import { Button, Spinner, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import type { ExtraKind, ExtraResult } from './registration-extras';
import { patientTabPath } from './patient-tabs';

export interface RegisteredPatient {
  id: string;
  mrn: string;
  fullName: string;
  details: string;
  quick: boolean;
}

interface SuccessAction {
  key: string;
  icon: LucideIcon;
  label: string;
  hint: string;
  onClick: () => void;
  busy?: boolean;
  primary?: boolean;
}

const EXTRA_LABEL: Record<ExtraKind, { saved: string; failed: string }> = {
  photo: { saved: 'extra_photo_saved', failed: 'extra_photo_failed' },
  insurance: { saved: 'extra_insurance_saved', failed: 'extra_insurance_failed' },
  card: { saved: 'extra_card_saved', failed: 'extra_card_failed' },
};

function extraFixPath(patientId: string, kind: ExtraKind): string {
  if (kind === 'insurance') return patientTabPath(patientId, 'insurance');
  if (kind === 'card') return patientTabPath(patientId, 'documents');
  return `/patients/${patientId}/edit`;
}

function ExtrasCard({
  patientId,
  extras,
  retrying,
  onRetry,
}: {
  patientId: string;
  extras: readonly ExtraResult[];
  retrying: ExtraKind | null;
  onRetry: (kind: ExtraKind) => void;
}) {
  return (
    <ul className="mx-4 space-y-2 sm:mx-6" aria-live="polite">
      {extras.map((extra) => (
        <li key={extra.kind} className="flex flex-wrap items-start gap-3 rounded-xl border bg-background px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">{tr('opd', extra.ok ? EXTRA_LABEL[extra.kind].saved : EXTRA_LABEL[extra.kind].failed)}</span>
              <StatusBadge
                status={extra.ok ? 'done' : 'failed'}
                tone={extra.ok ? 'success' : 'destructive'}
                label={tr('opd', extra.ok ? 'extra_state_saved' : 'extra_state_failed')}
              />
            </div>
            {!extra.ok && extra.error && <p className="mt-1 text-xs text-muted-foreground [overflow-wrap:anywhere]">{extra.error}</p>}
          </div>
          {!extra.ok && (
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" size="sm" variant="outline" loading={retrying === extra.kind} disabled={retrying !== null} onClick={() => onRetry(extra.kind)}>
                <RotateCcw className="h-4 w-4" aria-hidden />
                {tr('opd', 'extra_retry')}
              </Button>
              <Link to={extraFixPath(patientId, extra.kind)} className="text-xs font-medium text-primary underline-offset-2 hover:underline focus-ring rounded">
                {tr('opd', 'extra_fix_on_record')}
              </Link>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

export function RegistrationSuccess({
  patient,
  onRegisterAnother,
  extras = [],
  retrying = null,
  onRetry,
}: {
  patient: RegisteredPatient;
  onRegisterAnother: () => void;
  extras?: readonly ExtraResult[];
  retrying?: ExtraKind | null;
  onRetry?: (kind: ExtraKind) => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const { can, hasModule } = useAuth();
  const heading = useRef<HTMLHeadingElement>(null);
  const profilePath = `/patients/${patient.id}`;
  const canBook = hasModule('opd') && can('appointment.manage');
  const canComplete = can('patient.update');

  useEffect(() => {
    heading.current?.focus();
  }, []);

  const printCard = useMutation({
    mutationFn: () => get<{ html: string }>(`/patients/${patient.id}/card`),
    onSuccess: (document) => printDocument(document.html),
    onError: (error) => toast.error(tr('opd', 'could_not_print_the_card'), describeError(error)),
  });

  const copyMrn = async () => {
    if (await copyText(patient.mrn)) toast.success(tr('opd', 'mrn_copied'));
    else toast.error(tr('opd', 'mrn_copy_failed'));
  };

  const actions: SuccessAction[] = [
    ...(canBook
      ? [
          {
            key: 'walk-in',
            icon: DoorOpen,
            label: tr('opd', 'walk_in'),
            hint: tr('opd', 'success_walk_in_hint'),
            primary: true,
            onClick: () => navigate(`/appointments/walk-in?patientId=${patient.id}`),
          },
          {
            key: 'book',
            icon: CalendarPlus,
            label: tr('opd', 'book_appointment'),
            hint: tr('opd', 'success_book_hint'),
            onClick: () => navigate(`/appointments/new?patientId=${patient.id}&returnTo=${encodeURIComponent(profilePath)}`),
          },
        ]
      : []),
    {
      key: 'print',
      icon: Printer,
      label: tr('opd', 'print_patient_card'),
      hint: tr('opd', 'success_print_hint'),
      busy: printCard.isPending,
      onClick: () => printCard.mutate(),
    },
    {
      key: 'profile',
      icon: UserRound,
      label: tr('opd', 'open_profile'),
      hint: tr('opd', 'success_profile_hint'),
      onClick: () => navigate(profilePath),
    },
  ];

  return (
    <div className="mx-auto w-full max-w-3xl">
      <section className="overflow-hidden rounded-2xl border bg-card shadow-sm motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-300">
        <div className="bg-gradient-to-b from-success/15 via-success/5 to-transparent px-6 pb-6 pt-8 text-center">
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-success text-success-foreground shadow-lg shadow-success/30 motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:duration-500">
            <Check className="h-8 w-8" strokeWidth={2.5} aria-hidden />
          </span>
          <h1 ref={heading} tabIndex={-1} className="mt-4 text-xl font-semibold outline-none">
            {tr('opd', 'patient_registered')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{patient.fullName}</span>
            {patient.details ? ` · ${patient.details}` : ''}
          </p>
          <div className="mt-4 inline-flex items-center gap-3 rounded-xl border bg-background px-4 py-2 shadow-sm">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{tr('opd', 'mrn')}</span>
            <span className="font-mono text-lg font-semibold tracking-wider" dir="ltr">
              {patient.mrn}
            </span>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label={tr('opd', 'copy_mrn')} onClick={() => void copyMrn()}>
              <Copy className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>

        {extras.length > 0 && onRetry && <ExtrasCard patientId={patient.id} extras={extras} retrying={retrying} onRetry={onRetry} />}

        {patient.quick && (
          <div className="mx-4 flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-primary/40 bg-primary/5 px-4 py-3 sm:mx-6">
            <Sparkles className="h-5 w-5 shrink-0 text-primary" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{tr('opd', 'quick_saved_title')}</p>
              <p className="text-xs text-muted-foreground">{tr('opd', canComplete ? 'quick_saved_body' : 'quick_saved_body_no_edit')}</p>
            </div>
            {canComplete && (
              <Button type="button" size="sm" variant="outline" onClick={() => navigate(`/patients/${patient.id}/edit`)}>
                {tr('opd', 'complete_details')}
              </Button>
            )}
          </div>
        )}

        <div className="grid gap-3 p-4 sm:grid-cols-2 sm:p-6">
          {actions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.key}
                type="button"
                onClick={action.onClick}
                disabled={action.busy}
                className={
                  action.primary
                    ? 'group flex items-start gap-3 rounded-xl border border-primary/40 bg-primary/[0.06] p-4 text-start transition-[border-color,background-color,transform] duration-150 hover:border-primary hover:bg-primary/10 focus-ring active:scale-[0.99] disabled:opacity-60'
                    : 'group flex items-start gap-3 rounded-xl border bg-background p-4 text-start transition-[border-color,background-color,transform] duration-150 hover:border-muted-foreground/40 hover:bg-muted/40 focus-ring active:scale-[0.99] disabled:opacity-60'
                }
              >
                <span className={action.primary ? 'grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary text-primary-foreground' : 'grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary'}>
                  {action.busy ? <Spinner /> : <Icon className="h-5 w-5" aria-hidden />}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">{action.label}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{action.hint}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex justify-center border-t px-6 py-4">
          <Button type="button" variant="ghost" onClick={onRegisterAnother}>
            <UserPlus className="h-4 w-4" aria-hidden />
            {tr('opd', 'register_another_patient')}
          </Button>
        </div>
      </section>
    </div>
  );
}
